import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { PencilIcon } from '@heroicons/react/24/outline'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  SimpleGrid,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast
} from '@chakra-ui/react'
import { useAuthContext } from 'context/auth'
import {
  ReadinessAction,
  ReadinessDetail,
  ReadinessDateField,
  ReadinessMilestone,
  ReadinessState,
  readinessDate,
  readinessDateLabels,
  readinessErrorMessage,
  vacancyReadinessApi
} from 'lib/vacancy-readiness'
import ReadinessDateHistory from './ReadinessDateHistory'
import RentPricePanel, { RentPriceSummary } from './RentPricePanel'
import VacancySummary from './VacancySummary'
import ListingManagementPanel from './ListingManagementPanel'

type Editor = {
  field: ReadinessDateField
  action: ReadinessAction['action']
  milestone: ReadinessMilestone
  clearReason: 'mistake' | 'no_longer_ready'
  version: number
  legacyToken?: string
  legacyTourEstimate?: string | null
  legacyRentEstimate?: string | null
  tourEstimate: string
  rentEstimate: string
  initialTourEstimate: string
  initialRentEstimate: string
  date: string
  rentDate: string
  reason: string
  tourAttested: boolean
  rentAttested: boolean
  qcAcknowledged: boolean
  acknowledgedQcWarning: string | null
  correctsHistoryId?: string
  correctsRentHistoryId?: string
  originalTourDate: string | null
  originalRentDate: string | null
}
type PendingAttempt = {
  draftFingerprint: string
  command: ReadinessAction
  context: ReadinessDetail
}
const estimateChanges = (editor: Editor) => ({
  tour:
    editor.tourEstimate !== editor.initialTourEstimate ||
    (editor.field === 'estimated_tour_ready_date' &&
      !!editor.legacyToken &&
      (editor.tourEstimate || null) !== editor.legacyTourEstimate),
  rent:
    editor.rentEstimate !== editor.initialRentEstimate ||
    (editor.field === 'expected_move_in_date' &&
      !!editor.legacyToken &&
      (editor.rentEstimate || null) !== editor.legacyRentEstimate)
})

const milestoneName = (milestone: ReadinessMilestone) =>
  milestone === 'both' ? 'tour and rent' : milestone
const stateLabel = (state: ReadinessState) =>
  state === 'ready' ? 'Confirmed ready' : state === 'not_ready' ? 'Not ready' : 'Not yet confirmed'
const hasTour = (milestone: ReadinessMilestone) => milestone === 'tour' || milestone === 'both'
const hasRent = (milestone: ReadinessMilestone) => milestone === 'rent' || milestone === 'both'

const ReadinessPanel = ({
  vacancyPeriodId,
  onEditingChange,
  onReady,
  onSaved
}: {
  vacancyPeriodId: string
  onEditingChange: (editing: boolean) => void
  onReady?: () => void
  onSaved?: () => void
}) => {
  const { firebaseUser } = useAuthContext()
  const client = useQueryClient()
  const toast = useToast()
  const queryKey = ['vacancy-readiness', firebaseUser?.uid, vacancyPeriodId]
  const historyKey = ['vacancy-readiness-history', firebaseUser?.uid, vacancyPeriodId]
  const detailQuery = useQuery({
    queryKey,
    queryFn: () => vacancyReadinessApi.detail(vacancyPeriodId),
    enabled: !!firebaseUser
  })
  const [editor, setEditor] = useState<Editor | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const triggerRefs = useRef<Partial<Record<ReadinessDateField, HTMLButtonElement | null>>>({})
  const formRef = useRef<HTMLFormElement>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveConflict, setSaveConflict] = useState(false)
  const [reviewedLatest, setReviewedLatest] = useState(false)
  const retry = useRef<PendingAttempt | null>(null)
  const detail = detailQuery.data
  const [priceSummary, setPriceSummary] = useState<RentPriceSummary | null>(null)
  const [priceEditing, setPriceEditing] = useState(false)
  const [listingEditing, setListingEditing] = useState(false)
  const isEditing = !!editor || priceEditing || listingEditing
  const contentReady = !!detail || detailQuery.isError

  useEffect(() => {
    if (contentReady) onReady?.()
  }, [contentReady, onReady])

  useEffect(() => {
    onEditingChange(isEditing)
    return () => onEditingChange(false)
  }, [isEditing, onEditingChange])
  useEffect(() => {
    if (!isEditing) return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [isEditing])

  const editingField = editor?.field
  useEffect(() => {
    if (editingField)
      formRef.current?.querySelector<HTMLInputElement>('input[type="date"]')?.focus()
  }, [editingField])

  const restoreFocus = () => {
    const field = editor?.field
    if (field) requestAnimationFrame(() => triggerRefs.current[field]?.focus())
  }
  const begin = (
    action: Editor['action'],
    milestone: ReadinessMilestone,
    field: ReadinessDateField
  ) => {
    if (
      !detail ||
      editor ||
      priceEditing ||
      listingEditing ||
      !(detail.editable || (action === 'clear_readiness' && detail.can_report_loss))
    )
      return
    const correcting = action === 'correct_confirmation' || action === 'clear_readiness'
    const tourTarget = detail.confirmation_history_ids.tour
    const rentTarget = detail.confirmation_history_ids.rent
    if (correcting && ((hasTour(milestone) && !tourTarget) || (hasRent(milestone) && !rentTarget)))
      return
    setSaveError(null)
    setSaveConflict(false)
    setReviewedLatest(false)
    retry.current = null
    setEditor({
      field,
      action,
      milestone,
      clearReason: 'no_longer_ready',
      version: detail.version,
      legacyToken: detail.live_availability?.can_sync ? detail.live_availability.token : undefined,
      legacyTourEstimate: detail.live_availability?.estimated_tour_ready_date,
      legacyRentEstimate: detail.live_availability?.expected_move_in_date,
      tourEstimate: detail.estimated_tour_ready_date || '',
      rentEstimate: detail.expected_move_in_date || '',
      initialTourEstimate: detail.estimated_tour_ready_date || '',
      initialRentEstimate: detail.expected_move_in_date || '',
      date:
        (correcting && action !== 'clear_readiness'
          ? milestone === 'rent'
            ? detail.current_confirmed_rent_ready_date
            : detail.current_confirmed_tour_ready_date
          : null) || detail.today,
      rentDate: detail.current_confirmed_rent_ready_date || detail.today,
      reason: '',
      tourAttested: false,
      rentAttested: false,
      qcAcknowledged: false,
      acknowledgedQcWarning: null,
      correctsHistoryId: correcting
        ? (milestone === 'rent' ? rentTarget : tourTarget) ?? undefined
        : undefined,
      correctsRentHistoryId:
        correcting &&
        (milestone === 'both' ||
          (action === 'clear_readiness' &&
            milestone === 'tour' &&
            detail.current_confirmed_rent_ready_date))
          ? rentTarget ?? undefined
          : undefined,
      originalTourDate: detail.current_confirmed_tour_ready_date,
      originalRentDate: detail.current_confirmed_rent_ready_date
    })
  }
  const updateEditor = (patch: Partial<Editor>) => {
    if (savingRef.current) return
    // User edits start a new command; background query refreshes never do.
    retry.current = null
    setEditor((current) => (current ? { ...current, ...patch } : current))
  }
  const closeEditor = () => {
    if (savingRef.current) return
    restoreFocus()
    setEditor(null)
    setSaveError(null)
    setSaveConflict(false)
    retry.current = null
  }
  const refreshVersion = async () => {
    // This explicit refresh abandons any uncertain attempt while retaining the draft.
    updateEditor({})
    const refreshed = await detailQuery.refetch()
    if (refreshed.data && !refreshed.isError) {
      setReviewedLatest(true)
      if (
        ((editor?.action === 'correct_confirmation' || editor?.action === 'clear_readiness') &&
          ((hasTour(editor.milestone) &&
            refreshed.data.confirmation_history_ids.tour !== editor.correctsHistoryId) ||
            ((hasRent(editor.milestone) || !!editor.correctsRentHistoryId) &&
              refreshed.data.confirmation_history_ids.rent !==
                (editor.correctsRentHistoryId ?? editor.correctsHistoryId)))) ||
        (editor?.action === 'clear_readiness' &&
          editor.milestone === 'tour' &&
          refreshed.data.confirmation_history_ids.rent !== (editor.correctsRentHistoryId ?? null))
      ) {
        setSaveError(
          'A selected confirmation has changed. Your entries are still here; cancel and reopen the current confirmation before changing readiness.'
        )
        return
      }
      updateEditor({
        version: refreshed.data.version,
        legacyToken: refreshed.data.live_availability?.can_sync
          ? refreshed.data.live_availability.token
          : undefined,
        legacyTourEstimate: refreshed.data.live_availability?.estimated_tour_ready_date,
        legacyRentEstimate: refreshed.data.live_availability?.expected_move_in_date
      })
      setSaveConflict(false)
      setSaveError(null)
    } else {
      setSaveError('Could not refresh saved dates. Your entries are still here; please try again.')
    }
  }
  const save = async () => {
    if (!editor || !detail || savingRef.current) return
    const draftFingerprint = JSON.stringify(editor)
    let attempt = retry.current?.draftFingerprint === draftFingerprint ? retry.current : null
    if (!attempt) {
      const isEstimate = editor.action === 'edit_estimates'
      const { tour: tourChanged, rent: rentChanged } = estimateChanges(editor)
      const effectiveTour = tourChanged ? editor.tourEstimate : detail.estimated_tour_ready_date
      const effectiveRent = rentChanged ? editor.rentEstimate : detail.expected_move_in_date
      let validation: string | null = null
      if (isEstimate && !tourChanged && !rentChanged)
        validation = 'Change an estimated date before saving.'
      if (isEstimate && effectiveTour && effectiveRent && effectiveRent < effectiveTour)
        validation = 'Estimated rent readiness must be on or after estimated tour readiness.'
      if (!isEstimate && editor.action !== 'clear_readiness') {
        const pairedCorrection =
          editor.action === 'correct_confirmation' && editor.milestone === 'both'
        if (
          !editor.date ||
          editor.date > detail.today ||
          (pairedCorrection && (!editor.rentDate || editor.rentDate > detail.today))
        )
          validation = 'Enter actual dates on or before today in this home’s calendar.'
        else if (pairedCorrection && editor.rentDate < editor.date)
          validation = 'Actual rent readiness must be on or after actual tour readiness.'
        else if (
          (hasTour(editor.milestone) && !editor.tourAttested) ||
          (hasRent(editor.milestone) && !editor.rentAttested)
        )
          validation = 'Please confirm each selected readiness statement.'
        else if (
          detail.qc_warning &&
          (!editor.qcAcknowledged || editor.acknowledgedQcWarning !== detail.qc_warning)
        )
          validation = 'Please acknowledge the QC warning before confirming readiness.'
      }
      if (
        isEstimate &&
        detail.live_availability?.required &&
        (!detail.live_availability.can_sync || !editor.legacyToken)
      )
        validation =
          detail.live_availability.warning ||
          'Live listing dates need review before this estimate can be saved.'
      if (!editor.reason.trim()) validation = 'Add a note explaining this date change.'
      if (validation) {
        setSaveError(validation)
        return
      }
      const payload: Omit<ReadinessAction, 'client_key'> =
        editor.action === 'clear_readiness'
          ? {
              action: 'clear_readiness',
              expected_version: editor.version,
              milestone: editor.milestone,
              clear_reason: editor.clearReason,
              reason: editor.reason.trim(),
              ...(editor.clearReason === 'no_longer_ready' ? { effective_date: editor.date } : {}),
              corrects_history_id: editor.correctsHistoryId,
              ...(editor.correctsRentHistoryId
                ? { corrects_rent_history_id: editor.correctsRentHistoryId }
                : {})
            }
          : isEstimate
          ? {
              action: editor.action,
              expected_version: editor.version,
              ...(editor.legacyToken ? { expected_legacy_token: editor.legacyToken } : {}),
              ...(tourChanged ? { estimated_tour_ready_date: editor.tourEstimate || null } : {}),
              ...(rentChanged ? { expected_move_in_date: editor.rentEstimate || null } : {}),
              ...(editor.reason.trim() ? { reason: editor.reason.trim() } : {})
            }
          : {
              action: editor.action,
              expected_version: editor.version,
              milestone: editor.milestone,
              effective_date: editor.date,
              ...(editor.action === 'correct_confirmation' && editor.milestone === 'both'
                ? { rent_effective_date: editor.rentDate }
                : {}),
              reason: editor.reason.trim(),
              ...(hasTour(editor.milestone) ? { tour_attested: true } : {}),
              ...(hasRent(editor.milestone) ? { rent_attested: true } : {}),
              ...(detail.qc_warning ? { qc_acknowledged: true } : {}),
              ...(editor.correctsHistoryId
                ? { corrects_history_id: editor.correctsHistoryId }
                : {}),
              ...(editor.correctsRentHistoryId
                ? { corrects_rent_history_id: editor.correctsRentHistoryId }
                : {})
            }

      attempt = {
        draftFingerprint,
        command: { ...payload, client_key: uuid() },
        context: { ...detail, confirmation_history_ids: { ...detail.confirmation_history_ids } }
      }
      retry.current = attempt
    }
    savingRef.current = true
    setSaving(true)
    setSaveError(null)
    try {
      const result = await vacancyReadinessApi.act(vacancyPeriodId, attempt.command)
      client.setQueryData(queryKey, result)
      await Promise.all([
        client.invalidateQueries({ queryKey }),
        client.invalidateQueries({ queryKey: historyKey }),
        client.invalidateQueries({
          queryKey: ['vacancy-listing-management', firebaseUser?.uid, vacancyPeriodId]
        })
      ])
      restoreFocus()
      setEditor(null)
      retry.current = null
      setSaveConflict(false)
      onSaved?.()
      toast({ title: 'Readiness saved', status: 'success', duration: 4000, isClosable: true })
    } catch (error) {
      const e = error as { statusCode?: number; status?: number }
      setSaveError(readinessErrorMessage(error))
      setSaveConflict(e?.statusCode === 409 || e?.status === 409)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }
  if (detailQuery.isLoading) return <Spinner aria-label='Loading readiness' />
  if (!detail)
    return (
      <Alert status='error' flexDirection='column' alignItems='start' gap={2}>
        <Text>{readinessErrorMessage(detailQuery.error)}</Text>
        <Button size='sm' onClick={() => detailQuery.refetch()}>
          Try again
        </Button>
      </Alert>
    )
  const pendingAttempt =
    editor && retry.current?.draftFingerprint === JSON.stringify(editor) ? retry.current : null
  const formContext = pendingAttempt?.context ?? detail
  const isEstimate = editor?.action === 'edit_estimates'
  const isClearing = editor?.action === 'clear_readiness'

  const dateChanged =
    !!editor &&
    (isEstimate
      ? estimateChanges(editor).tour || estimateChanges(editor).rent
      : editor.action === 'clear_readiness' ||
        editor.action === 'confirm' ||
        (hasTour(editor.milestone) && editor.date !== editor.originalTourDate) ||
        (hasRent(editor.milestone) &&
          (editor.milestone === 'both' ? editor.rentDate : editor.date) !==
            editor.originalRentDate))
  const inlineEditor = editor && (
    <Box
      as='form'
      ref={formRef}
      noValidate={!!pendingAttempt}
      aria-label={`Update ${readinessDateLabels[editor.field].toLowerCase()}`}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault()
        void save()
      }}
      p={3}
      borderWidth={1}
      borderColor='teal.400'
      borderRadius='md'
    >
      <Stack spacing={4}>
        {editor && (
          <Stack spacing={4}>
            {editor.action === 'correct_confirmation' &&
              editor.milestone !== 'both' &&
              detail.confirmation_history_ids.tour &&
              detail.confirmation_history_ids.rent && (
                <Button
                  size='sm'
                  variant='link'
                  colorScheme='teal'
                  alignSelf='start'
                  isDisabled={saving}
                  onClick={() =>
                    updateEditor({
                      milestone: 'both',
                      date:
                        editor.milestone === 'tour'
                          ? editor.date
                          : detail.current_confirmed_tour_ready_date || detail.today,
                      rentDate:
                        editor.milestone === 'rent'
                          ? editor.date
                          : detail.current_confirmed_rent_ready_date || detail.today,
                      correctsHistoryId: detail.confirmation_history_ids.tour || undefined,
                      correctsRentHistoryId: detail.confirmation_history_ids.rent || undefined
                    })
                  }
                >
                  Adjust both actual dates
                </Button>
              )}
            {isClearing ? (
              <>
                <Heading size='sm'>Clear {editor.milestone} readiness</Heading>
                <FormControl isRequired>
                  <FormLabel htmlFor='readiness-clear-reason'>
                    Why are you clearing readiness?
                  </FormLabel>
                  <Select
                    id='readiness-clear-reason'
                    value={editor.clearReason}
                    isDisabled={saving}
                    onChange={(e) =>
                      updateEditor({ clearReason: e.target.value as Editor['clearReason'] })
                    }
                  >
                    <option value='no_longer_ready'>
                      Home is no longer {editor.milestone} ready
                    </option>
                    {detail.editable && <option value='mistake'>Marked ready by mistake</option>}
                  </Select>
                </FormControl>
                <Alert status='warning' display='block'>
                  {editor.clearReason === 'mistake'
                    ? 'Retracts the incorrect confirmation. It remains visible in history but no longer counts as a valid readiness confirmation.'
                    : 'Marks the home not ready and preserves the earlier ready period in history.'}
                  {editor.milestone === 'tour' && editor.originalRentDate
                    ? ' This also clears rent readiness because it depends on tour readiness.'
                    : editor.milestone === 'rent'
                    ? ' Tour readiness is unchanged.'
                    : ''}
                </Alert>
                {editor.clearReason === 'no_longer_ready' && (
                  <FormControl isRequired>
                    <FormLabel htmlFor='readiness-loss-date'>No longer ready as of</FormLabel>
                    <Input
                      id='readiness-loss-date'
                      type='date'
                      value={editor.date}
                      min={
                        (editor.milestone === 'tour'
                          ? editor.originalRentDate || editor.originalTourDate
                          : editor.originalRentDate) || undefined
                      }
                      max={
                        formContext.end_date && formContext.end_date < formContext.today
                          ? formContext.end_date
                          : formContext.today
                      }
                      isDisabled={saving}
                      onChange={(e) => updateEditor({ date: e.target.value })}
                    />
                  </FormControl>
                )}
              </>
            ) : isEstimate ? (
              <>
                <FormControl>
                  <FormLabel htmlFor='readiness-estimate'>
                    {editor.field === 'estimated_tour_ready_date'
                      ? 'Estimated tour-ready date'
                      : 'Estimated rent-ready date'}
                  </FormLabel>
                  <Input
                    id='readiness-estimate'
                    type='date'
                    value={
                      editor.field === 'estimated_tour_ready_date'
                        ? editor.tourEstimate
                        : editor.rentEstimate
                    }
                    isDisabled={saving}
                    onChange={(event) =>
                      updateEditor(
                        editor.field === 'estimated_tour_ready_date'
                          ? { tourEstimate: event.target.value }
                          : { rentEstimate: event.target.value }
                      )
                    }
                  />
                </FormControl>
                <Button
                  size='sm'
                  alignSelf='start'
                  variant='outline'
                  isDisabled={saving}
                  onClick={() =>
                    updateEditor(
                      editor.field === 'estimated_tour_ready_date'
                        ? { tourEstimate: '' }
                        : { rentEstimate: '' }
                    )
                  }
                >
                  Clear date
                </Button>
              </>
            ) : (
              <>
                {editor.action === 'correct_confirmation' && (
                  <Button
                    size='sm'
                    variant='outline'
                    alignSelf='start'
                    isDisabled={saving}
                    onClick={() =>
                      updateEditor({
                        action: 'clear_readiness',
                        version: detail.version,
                        originalTourDate: detail.current_confirmed_tour_ready_date,
                        originalRentDate: detail.current_confirmed_rent_ready_date,
                        milestone:
                          editor.field === 'current_confirmed_rent_ready_date' ? 'rent' : 'tour',
                        date: detail.today,
                        correctsHistoryId:
                          detail.confirmation_history_ids[
                            editor.field === 'current_confirmed_rent_ready_date' ? 'rent' : 'tour'
                          ] || undefined,
                        correctsRentHistoryId:
                          editor.field === 'current_confirmed_tour_ready_date' &&
                          detail.current_confirmed_rent_ready_date
                            ? detail.confirmation_history_ids.rent || undefined
                            : undefined
                      })
                    }
                  >
                    Clear readiness…
                  </Button>
                )}
                {editor.milestone === 'both' && editor.action === 'confirm' && (
                  <Alert status='info'>
                    <AlertIcon />
                    Tour readiness has not been confirmed. Confirm both milestones below for the
                    same date, or cancel and confirm tour readiness separately first.
                  </Alert>
                )}
                {editor.action === 'correct_confirmation' && editor.milestone === 'both' ? (
                  <>
                    <FormControl isRequired>
                      <FormLabel htmlFor='corrected-tour-date'>
                        Corrected actual tour-ready date
                      </FormLabel>
                      <Input
                        id='corrected-tour-date'
                        type='date'
                        min={formContext.start_date || undefined}
                        max={
                          formContext.end_date && formContext.end_date < formContext.today
                            ? formContext.end_date
                            : formContext.today
                        }
                        value={editor.date}
                        isDisabled={saving}
                        onChange={(event) => updateEditor({ date: event.target.value })}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel htmlFor='corrected-rent-date'>
                        Corrected actual rent-ready date
                      </FormLabel>
                      <Input
                        id='corrected-rent-date'
                        type='date'
                        min={editor.date || formContext.start_date || undefined}
                        max={
                          formContext.end_date && formContext.end_date < formContext.today
                            ? formContext.end_date
                            : formContext.today
                        }
                        value={editor.rentDate}
                        isDisabled={saving}
                        onChange={(event) => updateEditor({ rentDate: event.target.value })}
                      />
                    </FormControl>
                  </>
                ) : (
                  <FormControl isRequired>
                    <FormLabel htmlFor='actual-ready-date'>
                      Actual {milestoneName(editor.milestone)}-ready date
                    </FormLabel>
                    <Input
                      id='actual-ready-date'
                      type='date'
                      min={formContext.start_date || undefined}
                      max={
                        formContext.end_date && formContext.end_date < formContext.today
                          ? formContext.end_date
                          : formContext.today
                      }
                      value={editor.date}
                      isDisabled={saving}
                      onChange={(event) => updateEditor({ date: event.target.value })}
                    />
                  </FormControl>
                )}
                {hasTour(editor.milestone) && (
                  <Checkbox
                    isChecked={editor.tourAttested}
                    isDisabled={saving}
                    onChange={(event) => updateEditor({ tourAttested: event.target.checked })}
                  >
                    I confirm the home is safe, accessible and presentable for tours, with no major
                    unfinished work.
                  </Checkbox>
                )}
                {hasRent(editor.milestone) && (
                  <Checkbox
                    isChecked={editor.rentAttested}
                    isDisabled={saving}
                    onChange={(event) => updateEditor({ rentAttested: event.target.checked })}
                  >
                    I confirm all physical work needed for move-in is complete.
                  </Checkbox>
                )}
                {formContext.qc_warning && (
                  <>
                    <Alert status='warning'>
                      <AlertIcon />
                      {formContext.qc_warning}
                    </Alert>
                    <Checkbox
                      isChecked={
                        editor.qcAcknowledged &&
                        editor.acknowledgedQcWarning === formContext.qc_warning
                      }
                      isDisabled={saving}
                      onChange={(event) =>
                        updateEditor({
                          qcAcknowledged: event.target.checked,
                          acknowledgedQcWarning: formContext.qc_warning
                        })
                      }
                    >
                      I have reviewed this warning. My note below addresses the missing or
                      incomplete QC.
                    </Checkbox>
                  </>
                )}
              </>
            )}
            <FormControl isRequired>
              <FormLabel htmlFor='readiness-reason'>Note</FormLabel>
              <Textarea
                id='readiness-reason'
                value={editor.reason}
                maxLength={2000}
                isDisabled={saving}
                onChange={(event) => updateEditor({ reason: event.target.value })}
              />
            </FormControl>
            {!pendingAttempt && detail.version !== editor.version && (
              <Alert status='warning'>
                <AlertIcon />
                Saved readiness has changed since you opened this form. Your entries have been kept;
                saving will ask you to refresh first.
              </Alert>
            )}
            {reviewedLatest && (
              <Box p={3} bg='gray.50' borderWidth={1} borderRadius='md'>
                <Text fontWeight='semibold' fontSize='sm'>
                  Latest saved dates
                </Text>
                <Text fontSize='sm'>
                  Tour estimate: {readinessDate(detail.estimated_tour_ready_date)}
                </Text>
                <Text fontSize='sm'>
                  Rent estimate: {readinessDate(detail.expected_move_in_date)}
                </Text>
                <Text fontSize='sm'>
                  Actual tour readiness: {readinessDate(detail.current_confirmed_tour_ready_date)}
                </Text>
                <Text fontSize='sm'>
                  Actual rent readiness: {readinessDate(detail.current_confirmed_rent_ready_date)}
                </Text>
                <Text mt={2} fontSize='sm'>
                  {isEstimate
                    ? 'Your entries above have been kept. Only the estimated dates you changed will be saved.'
                    : 'Your confirmation entries above have been kept. Review them before saving.'}
                </Text>
              </Box>
            )}
            {pendingAttempt && saveError && !saveConflict && (
              <Alert status='info'>
                <AlertIcon />
                Your entries are unchanged. Retrying sends exactly the same save request, even if
                saved dates or QC have since changed. Edit an entry or cancel to start a new save.
              </Alert>
            )}
            {saveError && (
              <Alert status='error' flexDirection='column' alignItems='start' gap={2}>
                <Text>{saveError}</Text>
                {saveConflict && (
                  <>
                    <Text fontSize='sm'>
                      Your entries are still here. Review the latest saved dates before trying
                      again.
                    </Text>
                    <Button size='sm' onClick={refreshVersion} isLoading={detailQuery.isFetching}>
                      Refresh saved dates and keep my entries
                    </Button>
                  </>
                )}
              </Alert>
            )}
          </Stack>
        )}

        <HStack gap={3} flexWrap='wrap'>
          <Button variant='ghost' onClick={closeEditor} isDisabled={saving}>
            Cancel
          </Button>
          <Button
            colorScheme='teal'
            type='submit'
            isLoading={saving}
            isDisabled={
              (!pendingAttempt &&
                (!(detail.editable || (isClearing && detail.can_report_loss)) || !dateChanged)) ||
              saveConflict
            }
          >
            {isClearing
              ? 'Clear readiness'
              : isEstimate
              ? editor?.legacyToken
                ? 'Save estimate and live date'
                : 'Save estimate'
              : editor?.action === 'correct_confirmation'
              ? 'Save correction'
              : editor?.milestone === 'both'
              ? 'Confirm both milestones'
              : 'Confirm readiness'}
          </Button>
        </HStack>
      </Stack>
    </Box>
  )
  const renderDate = (
    field: ReadinessDateField,
    label: string,
    milestone: 'tour' | 'rent',
    estimate: boolean
  ) => (
    <Stack spacing={3} key={field}>
      {editor?.field === field ? (
        inlineEditor
      ) : detail.editable || (!estimate && detail[field] && detail.can_report_loss) ? (
        <Button
          ref={(node) => {
            triggerRefs.current[field] = node
          }}
          variant='outline'
          height='auto'
          minH='76px'
          p={3}
          whiteSpace='normal'
          textAlign='left'
          justifyContent='start'
          isDisabled={!!editor || priceEditing || listingEditing}
          aria-label={`${
            detail[field] ? 'Update' : estimate ? 'Add' : 'Confirm'
          } ${label.toLowerCase()}`}
          _hover={{ bg: 'teal.50', borderColor: 'teal.500' }}
          _focusVisible={{ boxShadow: 'outline' }}
          onClick={() =>
            begin(
              !detail.editable
                ? 'clear_readiness'
                : estimate
                ? 'edit_estimates'
                : detail[field]
                ? 'correct_confirmation'
                : 'confirm',
              !estimate &&
                !detail[field] &&
                milestone === 'rent' &&
                !detail.current_confirmed_tour_ready_date
                ? 'both'
                : milestone,
              field
            )
          }
        >
          <HStack justify='space-between' width='full' spacing={3}>
            <Stack spacing={1}>
              <Text fontSize='sm' fontWeight='normal' color='gray.600'>
                {label}
              </Text>
              <Text fontSize='lg' fontWeight='semibold' color='gray.800'>
                {detail[field] ? readinessDate(detail[field]) : 'Not set'}
              </Text>
            </Stack>
            <Box as={PencilIcon} boxSize={4} color='gray.500' flexShrink={0} aria-hidden='true' />
          </HStack>
        </Button>
      ) : (
        <Box p={3} borderWidth={1} borderRadius='md'>
          <Text fontSize='sm' color='gray.600'>
            {label}
          </Text>
          <Text fontSize='lg' fontWeight='semibold'>
            {detail[field] ? readinessDate(detail[field]) : 'Not set'}
          </Text>
        </Box>
      )}
      <ReadinessDateHistory
        key={`${vacancyPeriodId}:${field}`}
        vacancyPeriodId={vacancyPeriodId}
        field={field}
      />
    </Stack>
  )

  return (
    <Stack
      spacing={5}
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape' && editor && !saving) {
          event.preventDefault()
          event.stopPropagation()
          closeEditor()
        }
      }}
    >
      <Box>
        <Heading size='md'>
          {detail.home_id ? (
            <Link
              as={RouterLink}
              to={`/home/${detail.home_id}`}
              target='_blank'
              rel='noopener noreferrer'
              color='teal.700'
            >
              {detail.address_line_1}
            </Link>
          ) : (
            detail.address_line_1
          )}
        </Heading>
        <Text color='gray.600'>{detail.address_line_2}</Text>
        <HStack mt={2} spacing={2} flexWrap='wrap'>
          <Badge colorScheme={detail.episode_kind === 'initial' ? 'purple' : 'cyan'}>
            {detail.episode_kind === 'initial' ? 'Initial vacancy' : 'Rental vacancy'}
          </Badge>
          {detail.state === 'canceled' && <Badge colorScheme='gray'>Canceled</Badge>}
          <Text fontSize='sm'>
            {readinessDate(detail.start_date)} to{' '}
            {detail.end_date
              ? readinessDate(detail.end_date)
              : detail.state === 'current'
              ? 'Present'
              : 'N/A'}
          </Text>
        </HStack>
        <VacancySummary detail={detail} price={priceSummary} userId={firebaseUser?.uid} />
      </Box>
      {detailQuery.isError && (
        <Alert status='warning'>
          <AlertIcon />
          Could not refresh saved dates.{' '}
          <Button size='sm' ml={2} onClick={() => detailQuery.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {detail.estimate_warning && (
        <Alert status='warning'>
          <AlertIcon />
          {detail.estimate_warning}
        </Alert>
      )}
      {detail.reconciliation_message && (
        <Alert status={detail.state === 'canceled' ? 'info' : 'warning'}>
          <AlertIcon />
          {detail.reconciliation_message}
        </Alert>
      )}
      {!detail.editable && !detail.can_report_loss && !detail.reconciliation_message && (
        <Text color='gray.600'>You have read-only access to these readiness dates.</Text>
      )}
      <Stack
        spacing={4}
        borderWidth={1}
        borderTopWidth={3}
        borderTopColor='teal.400'
        borderRadius='lg'
        p={4}
      >
        <Heading size='sm' color='teal.800'>
          Readiness dates
        </Heading>
        {detail.live_availability?.can_sync &&
          (detail.live_availability.estimated_tour_ready_date !==
            detail.estimated_tour_ready_date ||
            detail.live_availability.expected_move_in_date !== detail.expected_move_in_date) && (
            <Alert status='warning' display='block'>
              Existing live listing dates differ: tour{' '}
              {readinessDate(detail.live_availability.estimated_tour_ready_date ?? null)}, move-in{' '}
              {readinessDate(detail.live_availability.expected_move_in_date ?? null)}. Saving an
              estimate updates only that date on the live listing.
            </Alert>
          )}
        {detail.live_availability?.warning && (
          <Alert status='warning'>{detail.live_availability.warning}</Alert>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {(['tour', 'rent'] as const).map((milestone) => {
            const state =
              milestone === 'tour' ? detail.readiness_state : detail.rent_readiness_state
            return (
              <Stack key={milestone} p={4} spacing={4} borderWidth={1} borderRadius='lg'>
                <HStack justify='space-between' flexWrap='wrap'>
                  <Heading size='sm'>{milestone === 'tour' ? 'Tour' : 'Rent'} ready</Heading>
                  <Badge
                    colorScheme={
                      state === 'ready' ? 'green' : state === 'not_ready' ? 'orange' : 'gray'
                    }
                  >
                    {stateLabel(state)}
                  </Badge>
                </HStack>
                {renderDate(
                  milestone === 'tour' ? 'estimated_tour_ready_date' : 'expected_move_in_date',
                  `Estimated ${milestone}-ready date`,
                  milestone,
                  true
                )}
                {renderDate(
                  milestone === 'tour'
                    ? 'current_confirmed_tour_ready_date'
                    : 'current_confirmed_rent_ready_date',
                  `Actual ${milestone}-ready date`,
                  milestone,
                  false
                )}
                <Box as='details' color='gray.600' fontSize='sm'>
                  <Box as='summary' cursor='pointer'>
                    What counts as {milestone} ready?
                  </Box>
                  <Text mt={2}>
                    {milestone === 'tour'
                      ? 'Safe, accessible and presentable for a tour, with no major unfinished work.'
                      : 'All physical work needed for move-in is complete.'}
                  </Text>
                </Box>
              </Stack>
            )
          })}
        </SimpleGrid>
      </Stack>
      <RentPricePanel
        key={vacancyPeriodId}
        vacancyPeriodId={vacancyPeriodId}
        disabled={!!editor || listingEditing}
        onEditingChange={setPriceEditing}
        onSummaryChange={setPriceSummary}
        onSaved={onSaved}
      />
      <ListingManagementPanel
        key={`listing-${vacancyPeriodId}`}
        vacancyPeriodId={vacancyPeriodId}
        disabled={!!editor || priceEditing}
        onEditingChange={setListingEditing}
        onSaved={() => {
          client.invalidateQueries({ queryKey })
          onSaved?.()
        }}
      />
    </Stack>
  )
}

export default ReadinessPanel
